import { useState, useCallback } from 'react';

const CHUNK_SIZE = 50 * 1024 * 1024; // 50 MB — must match backend MULTIPART_CHUNK_SIZE

interface UploadState {
  uploading: boolean;
  progress: number;   // 0–100
  error: string | null;
}

interface UploadedFile {
  id: string;
  originalName: string;
  fileName: string;
  fileSize: number;
  type: string;
  status: string;
}

// ── Presigned multipart upload (browser → S3 directly) ───────────────────────

interface PrepareResponse {
  mode: 's3' | 'local';
  fileId?: string;
  uploadId?: string;
  s3Key?: string;
  parts?: Array<{ partNumber: number; url: string }>;
}

async function uploadPresigned(
  file: File,
  projectId: string,
  token: string,
  onProgress: (pct: number, bytesUploaded: number, speed: number) => void,
  folderId?: string | null,
  sectionHint?: string,
): Promise<UploadedFile> {
  // Step 1 — ask backend for presigned URLs
  const prepRes = await fetch('/api/upload/prepare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      fileName:  file.name,
      fileSize:  file.size,
      projectId,
      mimeType:  file.type || 'application/octet-stream',
      // The section decides which safety limits apply, so prepare must know it
      // too — not just complete.
      sectionHint: sectionHint ?? null,
    }),
  });
  if (!prepRes.ok) {
    const errBody = await prepRes.json().catch(() => ({})) as { error?: string };
    throw new Error(errBody.error || 'Failed to prepare upload');
  }
  const prep: PrepareResponse = await prepRes.json();

  // Local dev fallback — backend has no S3, use old XHR path
  if (prep.mode === 'local') return uploadXhr(file, projectId, token, onProgress, folderId);

  const { fileId, uploadId, s3Key, parts } = prep as Required<PrepareResponse>;
  const completedParts: Array<{ partNumber: number; etag: string }> = [];

  try {
    // Step 2 — upload each part directly to S3
    let bytesUploaded = 0;
    let smoothedSpeed = 0;

    for (let i = 0; i < parts.length; i++) {
      const { partNumber, url } = parts[i];
      const start     = (partNumber - 1) * CHUNK_SIZE;
      const end       = Math.min(start + CHUNK_SIZE, file.size);
      const chunk     = file.slice(start, end);
      const chunkSize = end - start;

      const t0 = Date.now();
      const partRes = await fetch(url, {
        method: 'PUT',
        body:   chunk,
      });
      if (!partRes.ok) throw new Error(`Part ${partNumber} upload failed (${partRes.status})`);

      // S3 CORS must expose the ETag header (configured in AWS Console)
      const etag = partRes.headers.get('ETag');
      if (!etag) throw new Error(`Part ${partNumber} returned no ETag — check S3 CORS configuration`);

      completedParts.push({ partNumber, etag });
      bytesUploaded += chunkSize;

      const elapsed = (Date.now() - t0) / 1000;
      const chunkSpeed = elapsed > 0 ? chunkSize / elapsed : 0;
      // exponential moving average for smoother display
      smoothedSpeed = smoothedSpeed === 0 ? chunkSpeed : 0.4 * chunkSpeed + 0.6 * smoothedSpeed;

      onProgress(Math.round(((i + 1) / parts.length) * 100), bytesUploaded, smoothedSpeed);
    }

    // Step 3 — tell backend to finalise the multipart upload
    const completeRes = await fetch(`/api/upload/complete/${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        fileId,
        uploadId,
        s3Key,
        originalName: file.name,
        fileSize:     file.size,
        mimeType:     file.type || 'application/octet-stream',
        parts:        completedParts,
        folderId:     folderId ?? null,
        // Disambiguates .zip uploads: the 3D section means a mesh bundle,
        // the point-cloud section an RCP bundle.
        sectionHint:  sectionHint ?? null,
      }),
    });
    if (!completeRes.ok) {
      const err = await completeRes.json();
      throw new Error(err.error || 'Failed to complete upload');
    }
    const data = await completeRes.json();
    return data.file;

  } catch (err) {
    // Clean up the incomplete multipart upload in S3
    fetch('/api/upload/abort', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ uploadId, s3Key }),
    }).catch(() => {});
    throw err;
  }
}

// ── Legacy XHR upload (local dev — file goes through backend) ─────────────────

function uploadXhr(
  file: File,
  projectId: string,
  token: string,
  onProgress: (pct: number, bytesUploaded: number, speed: number) => void,
  folderId?: string | null,
): Promise<UploadedFile> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folderId', folderId);

    let lastLoaded = 0;
    let lastTime   = Date.now();
    let smoothedSpeed = 0;

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const now     = Date.now();
        const dt      = (now - lastTime) / 1000;
        const chunkSpeed = dt > 0 ? (e.loaded - lastLoaded) / dt : 0;
        smoothedSpeed    = smoothedSpeed === 0 ? chunkSpeed : 0.4 * chunkSpeed + 0.6 * smoothedSpeed;
        lastLoaded = e.loaded;
        lastTime   = now;
        onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, smoothedSpeed);
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve(JSON.parse(xhr.responseText).file);
      } else {
        reject(new Error(JSON.parse(xhr.responseText).error || 'Upload failed'));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.open('POST', `/api/upload/${projectId}`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useFileUpload = (projectId: string) => {
  const [state, setState] = useState<UploadState>({ uploading: false, progress: 0, error: null });

  const uploadFile = useCallback(
    async (
      file: File,
      onProgress?: (pct: number, bytesUploaded: number, speed: number) => void,
      folderId?: string | null,
      sectionHint?: string,
    ): Promise<UploadedFile> => {
      const token = localStorage.getItem('geodraft_token');
      if (!token) {
        const authErr = new Error('Not authenticated');
        setState((s) => ({ ...s, error: authErr.message }));
        throw authErr;
      }

      setState({ uploading: true, progress: 0, error: null });

      try {
        const uploaded = await uploadPresigned(file, projectId, token, (pct, bytesUploaded, speed) => {
          setState((s) => ({ ...s, progress: pct }));
          onProgress?.(pct, bytesUploaded, speed);
        }, folderId, sectionHint);
        setState({ uploading: false, progress: 100, error: null });
        return uploaded;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setState({ uploading: false, progress: 0, error: msg });
        throw err;
      }
    },
    [projectId],
  );

  const reset = () => setState({ uploading: false, progress: 0, error: null });

  return { ...state, uploadFile, reset };
};
